import { Component } from 'cc';
import Game from '../../Game';

export interface IExecutorOptions {
    timesPerFrame?: number;
    totalTimes?: number;
    dynamic?: boolean;
    dynamicAlgorithm?: (currentFPS: number) => number;
}

// 目標執行 FPS，當 dynamic 為 true 時會根據實際 FPS 調整每幀執行次數以達到此目標 FPS
const executeTargetFPS = 60;

export default class FrameActionExecutor<T> {
    private static readonly SCHEDULE_COMPONENT: Component = new Component(); // 用於排程的默認組件

    private _action: () => T;
    private _timesPerFrame: number;
    private _totalTimes: number;
    private _dynamic: boolean;

    private _baseTimesPerFrame: number;
    private _dynamicTimesPerFrame: number;
    private _currentTimes: number = 0;
    private _results: T[] = [];
    private _isStopped: boolean = false;
    private _frameAccumulator: number = 0;

    private _dynamicAlgorithm: (currentFPS: number) => number;
    private _resolvePromise?: (value: T[]) => void;

    /**
     * 分幀執行器
     * @param action 要執行的動作，應該是一個無參數且有返回值的函式
     * @param options 配置選項，包括每幀執行次數、總執行次數、是否動態調整等
     */
    constructor(action: () => T, options: IExecutorOptions = {}) {
        if (!action) {
            throw new Error('FrameActionExecutor: action is required');
        }

        this._action = action;
        this._timesPerFrame = options.timesPerFrame ?? 1;
        this._totalTimes = options.totalTimes ?? this._timesPerFrame;
        this._baseTimesPerFrame = this._timesPerFrame;
        this._dynamic = options.dynamic ?? false;
        this._dynamicAlgorithm = options.dynamicAlgorithm ?? ((currentFPS) => (this._baseTimesPerFrame * currentFPS) / executeTargetFPS);

        // 確保每幀執行次數不超過總次數
        this._timesPerFrame = Math.min(this._timesPerFrame, this._totalTimes);

        if (this._timesPerFrame <= 0 || this._totalTimes <= 0) {
            throw new Error('FrameActionExecutor: timesPerFrame and totalTimes should both be greater than 0');
        }

        this._dynamicTimesPerFrame = this._baseTimesPerFrame;
    }

    /**
     * 開始執行分幀動作
     * @returns 返回一個 Promise，在執行完畢或停止時 resolve 所有結果
     */
    public start(): Promise<T[]> {
        this._currentTimes = 0;
        this._results = [];
        this._isStopped = false;
        this._dynamicTimesPerFrame = this._baseTimesPerFrame;
        this._frameAccumulator = 0; // 開始時重置累積器

        return new Promise<T[]>((resolve) => {
            this._resolvePromise = resolve;

            // 開始調度
            FrameActionExecutor.SCHEDULE_COMPONENT.schedule(this.execute, 0);
            // 立即執行第一幀
            this.execute(0);
        });
    }

    /**
     * 停止當前的分幀執行
     */
    public stop(): void {
        if (this._isStopped) {
            return;
        }

        this._isStopped = true;
        this.cleanup();
    }

    /**
     * 每幀的核心執行邏輯（使用箭頭函式以自動綁定 this）
     */
    protected execute = (dt: number) => {
        let frameTime = Game.frameTime ?? 0; // 使用 Game 的 frameTime 以獲得更準確的時間測量

        if (this._dynamic && frameTime > 0 && dt > 0) {
            frameTime *= 0.001; // 轉換為秒
            const currentFPS = Math.min(240, 1 / frameTime);

            // 改用 _baseTimesPerFrame 作為計算基底
            // 這樣當 FPS 很低時，算出來的 _dynamicTimesPerFrame 可以是小於1的浮點數
            this._dynamicTimesPerFrame = this._dynamicAlgorithm(currentFPS);
        }

        // 將這一幀分配到的執行額度注入累積器
        this._frameAccumulator += this._dynamicTimesPerFrame;

        // 取出無條件捨去的整數，作為這一幀「真正執行」的次數
        let targetExecuteTimes = Math.floor(this._frameAccumulator);

        // 確保不超過總剩餘次數
        targetExecuteTimes = Math.min(targetExecuteTimes, this._totalTimes - this._currentTimes);

        // 執行動作
        for (let i = 0; i < targetExecuteTimes; i++) {
            if (this._isStopped) break;
            this._results.push(this._action());
            this._currentTimes++;
        }

        // 扣除這一幀已經用掉的整數額度，剩下的小數點（餘數）留到下一幀
        this._frameAccumulator -= targetExecuteTimes;

        if (this._currentTimes >= this._totalTimes || this._isStopped) {
            this.cleanup();
        }
    };

    /**
     * 清理排程並結束 Promise
     */
    protected cleanup(): void {
        FrameActionExecutor.SCHEDULE_COMPONENT.unschedule(this.execute);
        if (this._resolvePromise) {
            this._resolvePromise(this._results);
            this._resolvePromise = undefined;
        }
    }
}
